import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  alpha,
  useTheme,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_ACCEPT_LABEL,
  ATTACHMENT_MAX_BYTES,
  Visit,
  getRecordOptions,
  isAcceptedAttachment,
  uploadClientAttachment,
} from "../../services/clientRecordsService";
import { FileTypeIcon, fileSize } from "./fileDisplay";
import VisitPicker from "./VisitPicker";

type Props = {
  open: boolean;
  clientId: string;
  visits: Visit[];
  /** Pre-selects the visit when opened from one. */
  visitId?: string;
  onClose: () => void;
  onUploaded: () => void;
};

const errorText = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
};

// Every label sits on the border, filled or not, so the fields line up.
const fieldProps = { size: "small" as const, fullWidth: true, InputLabelProps: { shrink: true } };

/**
 * Adds a file to the client file: drop or pick it, give it a type (from the
 * organization's list, or a new one that joins the list) and a title, and
 * optionally tie it to a visit.
 */
const UploadFileDialog: React.FC<Props> = ({ open, clientId, visits, visitId, onClose, onUploaded }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const input = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [visit, setVisit] = useState("");
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setTitle("");
    setVisit(visitId ?? "");
    setError(null);
    getRecordOptions()
      .then((options) => {
        const list = options.fileTypes ?? [];
        setTypes(list);
        setType((current) => current || list[0] || "");
      })
      .catch(() => setTypes([]));
  }, [open, visitId]);

  // A thumbnail for photos the browser can show.
  useEffect(() => {
    if (!file || !file.type.startsWith("image/") || /hei[cf]$/i.test(file.name)) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const choose = (picked: File | null | undefined) => {
    if (!picked) return;
    if (!isAcceptedAttachment(picked)) {
      setError(`This kind of file cannot be added. Use a ${ATTACHMENT_ACCEPT_LABEL} file.`);
      return;
    }
    if (picked.size > ATTACHMENT_MAX_BYTES) {
      setError(`"${picked.name}" is ${fileSize(picked.size)}. Files can be up to 15 MB.`);
      return;
    }
    setError(null);
    // The title follows the file until someone types their own.
    const nameOf = (f: File) => f.name.replace(/\.[^.]+$/, "");
    if (!title.trim() || (file && title === nameOf(file))) setTitle(nameOf(picked));
    setFile(picked);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadClientAttachment(clientId, file, {
        category: type.trim() || "Other",
        title,
        appointmentId: visit || null,
      });
      // A new type was added to the organization's list on the server.
      if (type.trim() && !types.some((t) => t.toLowerCase() === type.trim().toLowerCase())) {
        getRecordOptions(true).catch(() => undefined);
      }
      onUploaded();
    } catch (err) {
      setError(errorText(err, "Could not upload the file. Check the connection and try again."));
    }
    setUploading(false);
  };

  const isNewType = type.trim() !== "" && !types.some((t) => t.toLowerCase() === type.trim().toLowerCase());

  return (
    <Dialog open={open} onClose={() => !uploading && onClose()} fullScreen={isPhone} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add a file</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2.25, pt: "8px !important" }}>
        <input
          ref={input}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          hidden
          onChange={(e) => {
            choose(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        {file ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            {preview ? (
              <Box component="img" src={preview} alt="" sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 2, flexShrink: 0 }} />
            ) : (
              <FileTypeIcon file={{ mimeType: file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "") }} size={56} />
            )}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }} noWrap>
                {file.name}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{fileSize(file.size)}</Typography>
            </Box>
            <Button size="small" onClick={() => input.current?.click()} disabled={uploading} sx={{ textTransform: "none" }}>
              Change
            </Button>
            <Tooltip title="Remove">
              <span>
                <IconButton size="small" aria-label="Remove file" onClick={() => setFile(null)} disabled={uploading}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => input.current?.click()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              choose(e.dataTransfer.files?.[0]);
            }}
            sx={{
              border: "2px dashed",
              borderColor: dragging ? "primary.main" : "#CBD5E1",
              bgcolor: dragging ? alpha(theme.palette.primary.main, 0.06) : "#F8FAFC",
              borderRadius: 3,
              py: 3.5,
              px: 2,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 150ms ease",
              "&:hover, &:focus-visible": { borderColor: "primary.main", outline: "none" },
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "primary.main" }} aria-hidden />
            <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
              Drop a file here or{" "}
              <Box component="span" sx={{ color: "primary.main", textDecoration: "underline" }}>
                browse
              </Box>
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.25 }}>
              {ATTACHMENT_ACCEPT_LABEL}, up to 15 MB
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "grid", gap: 2.25, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          <Autocomplete
            freeSolo
            options={types}
            inputValue={type}
            onInputChange={(_, value) => setType(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                {...fieldProps}
                label="Type"
                placeholder="Pick or type a new one"
                helperText={isNewType ? `"${type.trim()}" will be added to your file types.` : " "}
                inputProps={{ ...params.inputProps, maxLength: 60 }}
              />
            )}
          />
          <TextField
            {...fieldProps}
            label="Title"
            placeholder="e.g. Blood test March"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 150 }}
            helperText=" "
          />
        </Box>

        <VisitPicker visits={visits} value={visit} onChange={setVisit} disabled={uploading} />

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={uploading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!file || uploading}
          onClick={upload}
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none" }}
        >
          {uploading ? "Uploading…" : "Add file"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadFileDialog;
